import React from 'react';
import { toast as sonnerToast } from 'sonner';
import { Button } from './button';
import { Badge } from './badge';
import { Progress } from './progress';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Info, 
  RefreshCw, 
  X, 
  ExternalLink,
  Copy,
  Bug,
  WifiOff,
  Clock
} from 'lucide-react';
import { ErrorSeverity, ErrorTypes } from '../../lib/errorHandler';
import i18n from '../../i18n';

const t = (key, options) => i18n.t(key, options);

/**
 * Enhanced toast variants with custom styling and actions
 */
export class EnhancedToast {
  static success(title, options = {}) {
    return sonnerToast.success(title, {
      icon: <CheckCircle className="h-4 w-4" />,
      description: options.description,
      duration: options.duration || 4000,
      action: options.action,
      ...options
    });
  }

  static error(title, options = {}) {
    return sonnerToast.error(title, {
      icon: <XCircle className="h-4 w-4" />,
      description: options.description,
      duration: options.duration || 6000,
      action: options.action,
      ...options
    });
  }

  static warning(title, options = {}) {
    return sonnerToast.warning(title, {
      icon: <AlertTriangle className="h-4 w-4" />,
      description: options.description,
      duration: options.duration || 5000,
      action: options.action,
      ...options
    });
  }

  static info(title, options = {}) {
    return sonnerToast.info(title, {
      icon: <Info className="h-4 w-4" />,
      description: options.description,
      duration: options.duration || 4000,
      action: options.action,
      ...options
    });
  }

  static loading(title, options = {}) {
    return sonnerToast.loading(title, {
      description: options.description,
      duration: Infinity,
      ...options
    });
  }

  static promise(promise, options = {}) {
    return sonnerToast.promise(promise, {
      loading: {
        title: options.loading?.title || t('common.loading'),
        description: options.loading?.description,
        icon: <RefreshCw className="h-4 w-4 animate-spin" />
      },
      success: {
        title: options.success?.title || t('toastEnh.success'),
        description: options.success?.description,
        icon: <CheckCircle className="h-4 w-4" />
      },
      error: {
        title: options.error?.title || t('toastEnh.error'),
        description: options.error?.description,
        icon: <XCircle className="h-4 w-4" />
      },
      ...options
    });
  }

  /**
   * Enhanced error toast with severity-based styling and actions
   */
  static enhancedError(enhancedError, options = {}) {
    const { severity, type, title, message, canRetry, errorId } = enhancedError;
    
    const getDuration = () => {
      switch (severity) {
        case ErrorSeverity.LOW: return 3000;
        case ErrorSeverity.MEDIUM: return 5000;
        case ErrorSeverity.HIGH: return 8000;
        case ErrorSeverity.CRITICAL: return Infinity;
        default: return 5000;
      }
    };

    const ActionComponent = canRetry && options.onRetry ? (
      <Button
        size="sm"
        variant="outline"
        onClick={options.onRetry}
        className="h-6 px-2 text-xs"
      >
        <RefreshCw className="h-3 w-3 mr-1" />
        {t('errors.retry')}
      </Button>
    ) : undefined;

    const toastMethod = severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH ? 'error' : 
                       severity === ErrorSeverity.MEDIUM ? 'warning' : 'info';

    return sonnerToast[toastMethod](title, {
      id: errorId,
      description: (
        <div className="space-y-2">
          <p>{message}</p>
          {errorId && (
            <Badge variant="outline" className="text-xs">
              ID: {errorId}
            </Badge>
          )}
        </div>
      ),
      duration: getDuration(),
      action: ActionComponent,
      ...options
    });
  }

  /**
   * Network status toast
   */
  static networkStatus(isOnline, options = {}) {
    if (isOnline) {
      return this.success(t('errors.connRestoredTitle'), {
        description: t('errors.connRestoredDesc'),
        icon: <CheckCircle className="h-4 w-4" />,
        ...options
      });
    } else {
      return this.error(t('errors.connLostTitle'), {
        description: t('errors.connLostDesc'),
        icon: <WifiOff className="h-4 w-4" />,
        duration: Infinity,
        ...options
      });
    }
  }

  /**
   * Progress toast for long operations
   */
  static progress(title, progress, options = {}) {
    return sonnerToast.custom(
      <div className="flex items-center space-x-3 p-3">
        <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
        <div className="flex-1">
          <p className="font-medium text-sm">{title}</p>
          {options.description && (
            <p className="text-xs text-muted-foreground">{options.description}</p>
          )}
          <Progress value={progress} className="h-2 mt-2" />
          <p className="text-xs text-muted-foreground mt-1">{progress}%</p>
        </div>
      </div>,
      {
        duration: Infinity,
        ...options
      }
    );
  }

  /**
   * Confirmation toast with actions
   */
  static confirm(title, options = {}) {
    return sonnerToast.custom(
      <div className="p-3">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-sm">{title}</p>
            {options.description && (
              <p className="text-xs text-muted-foreground mt-1">{options.description}</p>
            )}
            <div className="flex space-x-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  sonnerToast.dismiss();
                  options.onCancel?.();
                }}
                className="h-6 px-2 text-xs"
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  sonnerToast.dismiss();
                  options.onConfirm?.();
                }}
                className="h-6 px-2 text-xs"
              >
                {t('toastEnh.confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>,
      {
        duration: Infinity,
        ...options
      }
    );
  }

  /**
   * Update existing toast
   */
  static update(id, newOptions) {
    return sonnerToast.custom(newOptions.content, {
      id,
      ...newOptions
    });
  }

  /**
   * Dismiss toast
   */
  static dismiss(id) {
    if (id) {
      sonnerToast.dismiss(id);
    } else {
      sonnerToast.dismiss();
    }
  }

  /**
   * Dismiss all toasts
   */
  static dismissAll() {
    sonnerToast.dismiss();
  }
}

/**
 * Custom toast components for specific use cases
 */
export const ToastComponents = {
  // API Error Toast
  ApiError: ({ error, onRetry, onReport }) => (
    <div className="p-3">
      <div className="flex items-start space-x-3">
        <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-sm text-red-300">{error.title}</p>
          <p className="text-xs text-red-400 mt-1">{error.message}</p>

          {error.context?.validationErrors && (
            <div className="mt-2">
              <p className="text-xs font-medium text-red-300">{t('toastEnh.validationErrors')}</p>
              <ul className="text-xs text-red-400 list-disc list-inside">
                {Object.values(error.context.validationErrors).map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="flex items-center space-x-2 mt-2">
            <Badge variant="destructive" className="text-xs">
              {error.type}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {error.errorId}
            </Badge>
          </div>
          
          <div className="flex space-x-2 mt-3">
            {error.canRetry && onRetry && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                className="h-6 px-2 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                {t('errors.retry')}
              </Button>
            )}
            {onReport && (
              <Button
                size="sm"
                variant="outline"
                onClick={onReport}
                className="h-6 px-2 text-xs"
              >
                <Bug className="h-3 w-3 mr-1" />
                {t('toastEnh.report')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  ),

  // Operation Progress Toast
  OperationProgress: ({ title, progress, details, onCancel }) => (
    <div className="p-3">
      <div className="flex items-center space-x-3">
        <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="font-medium text-sm">{title}</p>
            {onCancel && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancel}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {details && (
            <p className="text-xs text-muted-foreground mt-1">{details}</p>
          )}
          <div className="flex items-center space-x-2 mt-2">
            <Progress value={progress} className="flex-1 h-2" />
            <span className="text-xs text-muted-foreground min-w-[3ch]">{progress}%</span>
          </div>
        </div>
      </div>
    </div>
  ),

  // System Notification Toast
  SystemNotification: ({ title, message, type = 'info', actions = [] }) => {
    const icons = {
      success: <CheckCircle className="h-5 w-5 text-green-500" />,
      warning: <AlertTriangle className="h-5 w-5 text-orange-500" />,
      error: <XCircle className="h-5 w-5 text-red-500" />,
      info: <Info className="h-5 w-5 text-blue-500" />
    };

    return (
      <div className="p-3">
        <div className="flex items-start space-x-3">
          {icons[type]}
          <div className="flex-1">
            <p className="font-medium text-sm">{title}</p>
            <p className="text-xs text-muted-foreground mt-1">{message}</p>
            
            {actions.length > 0 && (
              <div className="flex space-x-2 mt-3">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={action.variant || "outline"}
                    onClick={action.onClick}
                    className="h-6 px-2 text-xs"
                  >
                    {action.icon && <span className="mr-1">{action.icon}</span>}
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default EnhancedToast; 